import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * LockContext — global "verrouillage" state (spec 2026-02-18).
 *
 * Semantics
 * ---------
 * • `isLocked` : true → an opaque overlay covers the app and the user must
 *   enter their 4-digit PIN to keep working.
 * • `lock()`    : manual trigger. Called by the "Bye bye" button when the
 *   user has configured a PIN (`user.has_lock_pin === true`).
 * • `unlock(pin)` : POSTs the PIN to `/me/lock-pin/verify` and clears the
 *   lock on success. Returns `{ ok, remaining? }`.
 *
 * Auto-lock
 * ---------
 * The provider tracks user activity : mousemove, keydown, touchstart, scroll
 * AND every axios API call (via the shared axios interceptor already
 * installed on `api`). After 10 minutes without activity, if the user has a
 * PIN configured, the app auto-locks. If no PIN is set, auto-lock is
 * completely disabled — the app behaves exactly as before.
 *
 * The lock state itself is persisted in `sessionStorage` so a hard refresh
 * of the tab keeps the lock overlay in place (mandatory security). Once the
 * user closes the tab, the session ends and the OTP screen takes over.
 */
const LockContext = createContext(null);

// Idle timeout (spec §5) — configurable if needs be.
const IDLE_MS = 10 * 60 * 1000; // 10 minutes
const STORAGE_KEY = "ledesk_lock_active";

export function LockProvider({ children }) {
  const { user } = useAuth();
  const [isLocked, setIsLocked] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === "1"; }
    catch { return false; }
  });

  const timerRef = useRef(null);
  const enabled = !!user?.has_lock_pin;

  const persist = useCallback((v) => {
    try {
      if (v) sessionStorage.setItem(STORAGE_KEY, "1");
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  const lock = useCallback(() => {
    if (!enabled) return;   // no-op when PIN is not configured
    setIsLocked(true);
    persist(true);
  }, [enabled, persist]);

  const unlock = useCallback(async (pin) => {
    try {
      const { data } = await api.post("/me/lock-pin/verify", { pin });
      if (data?.ok) {
        setIsLocked(false);
        persist(false);
        return { ok: true };
      }
      return { ok: false, remaining: data?.remaining ?? 0 };
    } catch (err) {
      // 429 → cooldown active.
      if (err.response?.status === 429) {
        return { ok: false, cooldown: true, message: err.response.data?.detail };
      }
      return { ok: false, error: err.response?.data?.detail || "Erreur inconnue." };
    }
  }, [persist]);

  // ---- Idle detector -------------------------------------------------------
  useEffect(() => {
    if (!enabled || isLocked) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      return;
    }
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(lock, IDLE_MS);
    };
    reset();
    // Spec 2026-07-17 decies point 8 — CORRECTIF timer d'inactivité :
    // On NE compte QUE les événements utilisateur réels (souris, clavier,
    // toucher, scroll, clic). Historiquement on écoutait aussi
    // `desk:api-activity` dispatché par l'axios interceptor, mais les polls
    // en arrière-plan (NotificationBell 30s, PerformanceIndex 30s, Sidebar
    // 30s, Chat) le déclenchaient en permanence → le timer était RESET
    // toutes les 30s et le verrouillage automatique ne se déclenchait
    // JAMAIS en pratique. Le retrait de ce listener rétablit le
    // comportement attendu : 10 min sans action réelle → verrouillage.
    const events = ["mousemove", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, isLocked, lock]);

  // If the user removes their PIN while locked → drop the overlay.
  useEffect(() => {
    if (!enabled && isLocked) {
      setIsLocked(false);
      persist(false);
    }
  }, [enabled, isLocked, persist]);

  return (
    <LockContext.Provider value={{ isLocked, lock, unlock, enabled }}>
      {children}
    </LockContext.Provider>
  );
}

export const useLock = () => {
  const ctx = useContext(LockContext);
  if (!ctx) return { isLocked: false, lock: () => {}, unlock: async () => ({ ok: false }), enabled: false };
  return ctx;
};
